import React, { useCallback, useMemo, useState } from "react";

import {
  getDefaultProfileType,
  ProfileType
} from "@platformsh/observability-graph/ContinuousProfiling";
import { getUnixTime } from "date-fns";

import useQueryParams from "Hooks/useQueryStrings";
import { timeframeToRange } from "src/organization/pages/project/environment/metrics/components/Chart/settings";
import useActivityToggle from "src/organization/pages/project/environment/metrics/hooks/useActivityToggle";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import { MetricsContext } from "src/organization/pages/project/environment/metrics/hooks/useMetricsContext";
import usePinnedTabsSync from "src/organization/pages/project/environment/metrics/hooks/usePinnedTabsSync";
import useUpdateQueryParams from "src/organization/pages/project/environment/metrics/hooks/useUpdateQueryParams";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { getPollInterval } from "src/organization/pages/project/environment/metrics/utils/polling/utils";

import ComparisonTimeFrameIndicator from "./ComparisonTimeFrameIndicator";
import * as S from "./Conprof.styles";
import Controls from "./control/Controls";
import ComparisonFlamebearer from "./flamebearer/ComparisonFlamebearer";
import ComparisonFlamebearerSkeleton from "./flamebearer/ComparisonFlamebearerSkeleton";
import Flamebearer from "./flamebearer/Flamebearer";
import FlamebearerSkeleton from "./flamebearer/FlamebearerSkeleton";
import { ConprofDataContext } from "./hooks/useConprofContext";
import useConprofFlamebearerPoll from "./hooks/useConprofFlamebearerPoll";
import useConprofProfileTypesPoll from "./hooks/useConprofProfileTypesPoll";
import useConprofTimelinePoll from "./hooks/useConprofTimelinePoll";
import Timeline from "./timeline/Timeline";
import TimelineSkeleton from "./timeline/TimelineSkeleton";
import { ComparisonTimeFrame } from "./types";

const QueryParamComparisonStartA = "from-a";
const QueryParamComparisonEndA = "to-a";
const QueryParamComparisonStartB = "from-b";
const QueryParamComparisonEndB = "to-b";
const QueryParamProfileTypeName = "profile_type";

const Conprof = () => {
  const service = "ingester"; // hardcoded in sandbox only

  const [isDeployment, setDeployment] = useActivityToggle();
  const [timeFrame, setTimeFrame] = useState<TimeFrameType>(
    useInitialTimeframe({ id: "1h" })
  );

  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    timeFrame: timeFrame,
    setTimeFrame
  });

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeFrame);
  };

  const {
    [QueryParamComparisonStartA]: urlComparisonStartA,
    [QueryParamComparisonEndA]: urlComparisonEndA,
    [QueryParamComparisonStartB]: urlComparisonStartB,
    [QueryParamComparisonEndB]: urlComparisonEndB,
    [QueryParamProfileTypeName]: urlProfileTypeName
  } = useQueryParams([
    QueryParamComparisonStartA,
    QueryParamComparisonEndA,
    QueryParamComparisonStartB,
    QueryParamComparisonEndB,
    QueryParamProfileTypeName
  ]);
  const [comparisonTimeframe, setComparisonTimeframe] = useState<
    ComparisonTimeFrame | undefined
  >(
    urlComparisonStartA &&
      urlComparisonEndA &&
      urlComparisonStartB &&
      urlComparisonEndB
      ? {
          sideA: {
            from: new Date(parseInt(urlComparisonStartA) * 1000),
            to: new Date(parseInt(urlComparisonEndA) * 1000)
          },
          sideB: {
            from: new Date(parseInt(urlComparisonStartB) * 1000),
            to: new Date(parseInt(urlComparisonEndB) * 1000)
          }
        }
      : undefined
  );

  const { update } = useUpdateQueryParams({});

  const [profileUpdate, setProfileUpdate] = useState<ProfileType | undefined>();

  const { data: application } = useConprofProfileTypesPoll({
    service
  });

  const profileType = useMemo(() => {
    if (!application || profileUpdate) return profileUpdate;
    let type = application?.profile_types[urlProfileTypeName || ""];
    if (type) return type;

    type = getDefaultProfileType(application);
    update({ [QueryParamProfileTypeName]: type?.name });
    return type;
  }, [application, update, urlProfileTypeName, profileUpdate]);

  const timeLine = useConprofTimelinePoll({
    applicationName: service,
    profileTypeName: profileType?.name,
    timeFrame
  });

  const flamebearer = useConprofFlamebearerPoll({
    application,
    profileTypeName: profileType?.name,
    timeFrame
  });

  const flamebearerSideA = useConprofFlamebearerPoll({
    application,
    profileTypeName: profileType?.name,
    timeFrame: comparisonTimeframe?.sideA
  });

  const flamebearerSideB = useConprofFlamebearerPoll({
    application,
    profileTypeName: profileType?.name,
    timeFrame: comparisonTimeframe?.sideB
  });

  const pollInterval = getPollInterval(timeFrame);

  const handleProfileTypeChange = useCallback(
    (newProfileType: ProfileType | undefined) => {
      setProfileUpdate(newProfileType);
      update({ [QueryParamProfileTypeName]: newProfileType?.name });
    },
    [update]
  );
  const handleComparisonModeChange = useCallback(
    (newComparisonMode: boolean) => {
      if (!newComparisonMode) {
        setComparisonTimeframe(undefined);
        update({
          [QueryParamComparisonStartA]: undefined,
          [QueryParamComparisonEndA]: undefined,
          [QueryParamComparisonStartB]: undefined,
          [QueryParamComparisonEndB]: undefined
        });
      } else {
        const { start, end } = timeframeToRange(timeFrame);
        const middle = Math.floor(
          start.getTime() + (end.getTime() - start.getTime()) / 2
        );
        const comparisonTimeframe: ComparisonTimeFrame = {
          sideA: {
            from: new Date(start),
            to: new Date(middle)
          },
          sideB: {
            from: new Date(middle),
            to: new Date(end)
          }
        };
        setComparisonTimeframe(comparisonTimeframe);
        setTimeFrame({ from: start, to: end });
        update({
          [QueryParamComparisonStartA]: getUnixTime(
            comparisonTimeframe.sideA.from
          ).toString(),
          [QueryParamComparisonEndA]: getUnixTime(
            comparisonTimeframe.sideA.to
          ).toString(),
          [QueryParamComparisonStartB]: getUnixTime(
            comparisonTimeframe.sideB.from
          ).toString(),
          [QueryParamComparisonEndB]: getUnixTime(
            comparisonTimeframe.sideB.to
          ).toString()
        });
      }
    },
    [update, setTimeFrame, timeFrame]
  );

  const handleTimeframeChange = useCallback(
    (newTimeframe: TimeFrameType) => {
      setTimeFrame(newTimeframe);
      if (newTimeframe.id) {
        setComparisonTimeframe(undefined);
        update({
          [QueryParamComparisonStartA]: undefined,
          [QueryParamComparisonEndA]: undefined,
          [QueryParamComparisonStartB]: undefined,
          [QueryParamComparisonEndB]: undefined
        });
      }
    },
    [update, setTimeFrame]
  );

  const handleComparisonTimeframeChanged = useCallback(
    (newComparisonTimeframe: ComparisonTimeFrame) => {
      setComparisonTimeframe(newComparisonTimeframe);
      update({
        [QueryParamComparisonStartA]: getUnixTime(
          newComparisonTimeframe.sideA.from
        ).toString(),
        [QueryParamComparisonEndA]: getUnixTime(
          newComparisonTimeframe.sideA.to
        ).toString(),
        [QueryParamComparisonStartB]: getUnixTime(
          newComparisonTimeframe.sideB.from
        ).toString(),
        [QueryParamComparisonEndB]: getUnixTime(
          newComparisonTimeframe.sideB.to
        ).toString()
      });
    },
    [update]
  );

  const contextData: ConprofDataContext = useMemo(
    () => ({
      application,
      profileType,
      timelineData: timeLine.data,
      flamebearerData: flamebearer.data,
      flamebearerSideAData: flamebearerSideA.data,
      flamebearerSideBData: flamebearerSideB.data
    }),
    [
      application,
      profileType,
      timeLine.data,
      flamebearer.data,
      flamebearerSideA.data,
      flamebearerSideB.data
    ]
  );

  const showTimeline = !timeLine.status.refresh;
  const refreshA = flamebearerSideA.status.refresh;
  const refreshB = flamebearerSideB.status.refresh;
  const showComparison = !refreshB || !refreshA;

  const showFlamebearer = !flamebearer.status.refresh;
  const profileTypes = application?.profile_types || {};

  return (
    <MetricsContext.Provider
      value={{ data: contextData, timeFrame, setTimeFrame, isDeployment }}
    >
      <S.Layout profileTypeName={profileType?.name}>
        <Controls
          timeframeProps={{
            isUpdating:
              flamebearer.status.isLoading || timeLine.status.isLoading,
            pollInterval,
            lastUpdated: flamebearer.lastUpdated,
            timeframe: {
              timeframe: timeFrame,
              onChange: handleTimeframeChange
            },
            tabSync: {
              onSyncChange: handleSyncChange,
              isSynced,
              canJoinSync
            },
            deployment: {
              enabled: isDeployment,
              onChange: setDeployment
            }
          }}
          profileTypesProps={{
            profileTypes,
            profileType,
            onChange: handleProfileTypeChange
          }}
          comparisonModeProps={{
            isComparisonMode: !!comparisonTimeframe,
            onChange: handleComparisonModeChange
          }}
        />
        {comparisonTimeframe && (
          <ComparisonTimeFrameIndicator
            comparisonTimeframe={comparisonTimeframe}
          />
        )}
        {showTimeline ? (
          <Timeline
            comparisonTimeframe={comparisonTimeframe}
            onComparisonTimeframeChanged={handleComparisonTimeframeChanged}
          />
        ) : (
          <TimelineSkeleton />
        )}
        <hr />
        {comparisonTimeframe ? (
          <>
            {showComparison ? (
              <ComparisonFlamebearer />
            ) : (
              <ComparisonFlamebearerSkeleton />
            )}
          </>
        ) : (
          <>{showFlamebearer ? <Flamebearer /> : <FlamebearerSkeleton />}</>
        )}
      </S.Layout>
    </MetricsContext.Provider>
  );
};

export default Conprof;
